import { useEffect, useRef } from "react";

const SCRIPT_ID = "cf-turnstile-api";
const SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

const IS_DEV = process.env.NODE_ENV !== "production";

// eslint-disable-next-line no-console
const devWarn = (...args) => IS_DEV && console.warn(...args);

/**
 * Load the Turnstile ``api.js`` script exactly once per page and resolve
 * with the global ``window.turnstile`` when it's ready. Reused across
 * every widget instance so re-mounts don't refetch the script.
 */
function loadTurnstileScript() {
  return new Promise((resolve, reject) => {
    if (window.turnstile) {
      resolve(window.turnstile);
      return;
    }
    let s = document.getElementById(SCRIPT_ID);
    if (!s) {
      s = document.createElement("script");
      s.id = SCRIPT_ID;
      s.src = SCRIPT_SRC;
      s.async = true;
      s.defer = true;
      document.head.appendChild(s);
    }
    s.addEventListener("load", () => resolve(window.turnstile));
    s.addEventListener("error", () =>
      reject(new Error("Turnstile script failed to load"))
    );
  });
}

/**
 * Safely remove a rendered Turnstile widget when the host component
 * unmounts. Never throws — a widget can already be gone (e.g. after a
 * challenge was completed) and that's fine.
 */
function removeWidget(widgetId) {
  if (!window.turnstile || widgetId === null) return;
  try {
    window.turnstile.remove(widgetId);
  } catch (err) {
    devWarn("Turnstile.remove failed (widget already gone?)", err);
  }
}

/**
 * Cloudflare Turnstile widget wrapper.
 *
 * Renders a Turnstile challenge inside a container div and calls
 * ``onToken(token)`` when the visitor is verified. If
 * ``REACT_APP_TURNSTILE_SITE_KEY`` isn't configured, the component
 * renders nothing and the parent form works exactly as before — useful
 * for local development.
 *
 * Props:
 *   - onToken(token: string): fired when the widget successfully verifies
 *   - onError(): optional; fired when the visitor fails the challenge
 *   - theme: "auto" | "light" | "dark" (default "light" — matches our forms)
 */
export const TurnstileWidget = ({
  onToken,
  onError,
  theme = "light",
  className,
}) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;

  useEffect(() => {
    if (!siteKey || !containerRef.current) return undefined;
    let cancelled = false;

    loadTurnstileScript()
      .then((turnstile) => {
        if (cancelled || !turnstile || !containerRef.current) return;
        widgetIdRef.current = turnstile.render(containerRef.current, {
          sitekey: siteKey,
          theme,
          callback: (token) => onToken?.(token),
          "error-callback": (code) => onError?.(String(code || "")),
          "expired-callback": () => onToken?.(""),
          "timeout-callback": () => onToken?.(""),
        });
      })
      .catch((err) => {
        devWarn("Turnstile failed to initialise", err);
        onError?.();
      });

    return () => {
      cancelled = true;
      removeWidget(widgetIdRef.current);
    };
    // Callback identity isn't part of the widget lifecycle — re-rendering
    // the widget on every parent render would trap users in a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteKey, theme]);

  if (!siteKey) return null;

  return (
    <div
      ref={containerRef}
      data-testid="turnstile-widget"
      className={className}
    />
  );
};

export default TurnstileWidget;
